import { useState } from 'react';
import { OptionLabel } from './components/option-label';
import { SortableMenuItem } from './components/sortable-menu-item';
import { Grid, Select } from '@mui/material';
import CustomTooltip from 'components/custom-tooltip';
import { SelectionOptionDragOverlay } from './components/selection-option-drag-overlay';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import AngleDownNewIcon from 'assets/icons/Chevron-down_New_Icon.svg';
import OutlinedInput from 'components/inputs/outlined-input';
import ActionButton from 'components/buttons';
import { FieldArrayWithId, FieldErrors, useFormContext } from 'react-hook-form';
import { ISelectionFormValues } from 'widgets/selections/utils/types';
import { styles } from './styles';

export interface ISelectionOptionItem {
  fields: FieldArrayWithId<Pick<ISelectionFormValues, 'options'>, 'options', 'key'>[];
  isAddButtonDisabled: boolean;
  isSelectionApproved: boolean;
  tooltipTitle: string;
  activeOptionId: string;
  activeOptionIndex: number;
  errorsOptions: FieldErrors<Pick<ISelectionFormValues, 'options'>>;
  moveOption: (indexA: number, indexB: number) => void;
  activateOption: (orderNumber: number) => void;
  addOption: () => void;
}

const SelectionOptionItem = ({
  fields,
  isAddButtonDisabled,
  isSelectionApproved,
  tooltipTitle,
  activeOptionId,
  errorsOptions,
  activeOptionIndex,
  moveOption,
  activateOption,
  addOption,
}: ISelectionOptionItem) => {
  const [open, setOpen] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  const { activeId, onDragStart, onDragEnd, scrollToSelectedOption } = useDragAndDrop({
    fields,
    moveOption,
  });

  const form = useFormContext<Pick<ISelectionFormValues, 'options'>>();

  const activeOptionTitle = form.watch(`options.${activeOptionIndex}.title`);

  const onOpen = () => {
    setOpen(true);
    scrollToSelectedOption();
  };

  const onClose = () => {
    setOpen(false);
  };

  const onSelectChange = (id: string) => {
    const index = fields.findIndex((f) => f.id === id);
    activateOption(index);
  };

  const renderValue = (selected: string) => {
    const index = fields.findIndex((f) => f.id === selected);

    return (
      <OptionLabel
        text={activeOptionTitle || 'Untitled Option'}
        hasError={!!errorsOptions?.[index]}
      />
    );
  };

  const lessThanOneOption = fields.length <= 1;

  if (lessThanOneOption) return null;

  return (
    <Grid sx={styles.container}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <Select
          MenuProps={{
            PaperProps: {
              sx: styles.paper,
            },
          }}
          fullWidth
          open={open}
          value={activeOptionId}
          IconComponent={AngleDownNewIcon}
          input={<OutlinedInput />}
          renderValue={renderValue}
          onOpen={onOpen}
          onClose={onClose}
          onChange={(e) => onSelectChange(e.target.value)}
          sx={{
            cursor: 'pointer',
          }}
        >
          <SortableContext items={fields.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {fields.map((field, index) => (
              <SortableMenuItem
                key={field.id}
                index={index}
                field={field}
                errorsOptions={errorsOptions}
                canMove={fields.length > 1 && !isSelectionApproved}
                selectedValue={activeOptionId}
                onSelect={onSelectChange}
                onClose={onClose}
              />
            ))}
          </SortableContext>
        </Select>

        <SelectionOptionDragOverlay activeIndex={fields.findIndex((f) => f.id === activeId)} />
      </DndContext>
    </Grid>
  );
};

export default SelectionOptionItem;
